import { computed } from 'vue'

import { useLevelUp2026 } from '../../core-layer/composables/useLevelUp2026'
import { useLocation } from '../../location-layer/composables/useLocation'

import { APP_ACCESS_CONFIG, getAppIdFromPath } from '../config/app-visibility.config'
import type { AccessContext, AppId } from '../types/app-access'

/**
 * Composable for portal module visibility in the ClientPortal (sidebar, dashboard widgets, default landing).
 * It does not gate route loading by itself: direct URLs stay reachable unless the route adds a guard
 * (e.g. `require-discover-access` on `/discover`).
 *
 * Evaluation order for each app:
 *   1. If enabled is false → deny access (no further checks)
 *   2. If permission is set → require it to be true in locationPermissions; empty/undefined skips this check
 *   3. Run every condition in the app's `conditions` array (AND — all must pass)
 */
export const useAppAccess = () => {
  const { store: locationStore } = useLocation()
  const { isLevelUp2026Enabled } = useLevelUp2026()

  const canAccessApp = (appId: AppId): boolean => {
    const rule = APP_ACCESS_CONFIG[appId]
    if (rule.enabled === false) return false

    const context: AccessContext = {
      appId,
      locationPermissions: locationStore.locationPermissions,
      isLevelUp2026Enabled: isLevelUp2026Enabled.value,
    }

    if (rule.permission) {
      const hasPermission = locationStore.locationPermissions[rule.permission] ?? false
      if (!hasPermission) return false
    }

    if (rule.conditions?.length) {
      return rule.conditions.every(condition => condition(context))
    }

    return true
  }

  const canAccessAppRef = computed(() => (appId: AppId) => canAccessApp(appId))

  /**
   * Filter navigation items to only those the user has access to
   */
  const getAccessibleNavigationItems = <T extends { id: string }>(items: T[]): T[] => {
    return items.filter((item): item is T => canAccessApp(item.id as AppId))
  }

  return {
    canAccessApp,
    canAccessAppRef,
    getAccessibleNavigationItems,
    getAppIdFromPath,
    accessConfig: APP_ACCESS_CONFIG,
  }
}
